import styled from "@emotion/styled";
import { Button, DatePicker, Divider, Form, Input, InputNumber, message, Radio, Select } from "antd";
import moment from "moment";
import { FC, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import MyEditor from "src/common/components/MyEditor";
import { UploadImg } from "src/common/components/UploadImg";
import { MultiSelectRole } from "src/module/admin/components/MultiSelectRole";
import { MultiSelectPayWay } from "src/module/finance/component/MultiSelectPayWay";
import SubscriptionCascader from "src/module/membershipCard/component/subscriptionCascader";
import subscriptionCouponService from "src/module/subscriptionCoupon/service";

export const SubscriptionCouponModify: FC = () => {
	const navigate = useNavigate();
	const parseId = useParams().id;
	const { Item: FormItem } = Form;
	const { t } = useTranslation();
	const [formRef] = Form.useForm();
	const couponOption = [
		{ label: t("priceOffCoupon"), value: 1 },
		{ label: t("discountCoupon"), value: 2 },
		{ label: t("delayCoupon"), value: 3 },
	];
	const EffectiveWayOption = [
		{
			label: "过期日期",
			value: 1,
		},
		{
			label: "过期天数",
			value: 2,
		},
	];

	const init = useCallback(async () => {
		if (!parseId) return;
		const { data: res } = await subscriptionCouponService.detail(parseInt(parseId));
		if (res.code === 200) {
			const finalData = {
				...res.data,
				startTime: moment(res.data.startTime),
				...(res.data.effectiveWay === 1 && { expirationTime: moment(res.data.expirationTime) }),
			};
			formRef.setFieldsValue(finalData);
		}
	}, [parseId, formRef]);

	useEffect(() => {
		init();
	}, [init]);

	const submit = async (e: any) => {
		await formRef.validateFields();
		const finalVales = {
			...e,
			startTime: e.startTime?.format("YYYY-MM-DD 00:00:00"),
			worth: Number(e.worth || ""),
			...(e.effectiveWay === 1 && { expirationTime: e.expirationTime?.format("YYYY-MM-DD 23:59:59") }),
		};
		let response: any = {};
		if (!parseId) {
			response = await subscriptionCouponService.add(finalVales);
		} else {
			response = await subscriptionCouponService.update(parseInt(parseId), finalVales);
		}
		if (response.data.code !== 200) {
			message.error(response.data.msg);
			return;
		}
		navigate("/subscription-coupon/list");
	};

	return (
		<Container>
			<Form layout='vertical' labelAlign='left' labelCol={{ span: 4 }} onFinish={submit} form={formRef}>
				<Divider style={{ color: "#888" }}>{t("basic") + t("info")}</Divider>
				<FormItem label={t("couponName")} required name='title' rules={[{ required: true }]}>
					<Input />
				</FormItem>

				<FormItem label={t("couponType")} name='couponType' required rules={[{ required: true }]}>
					<Select options={couponOption} />
				</FormItem>
				<FormItem dependencies={["couponType"]}>
					{({ getFieldValue }) => {
						const couponType = getFieldValue("couponType");
						if (couponType === 1) {
							return (
								<>
									<FormItem label='满' name='maxMoney'>
										<InputNumber controls={false} addonAfter='元' required />
									</FormItem>
									<FormItem label='抵扣' name='deductMoney'>
										<InputNumber controls={false} addonAfter='元' required />
									</FormItem>
								</>
							);
						} else if (couponType === 2) {
							return (
								<>
									<FormItem label='折扣(打)' name='discount'>
										<InputNumber controls={false} min={0} max={1} addonAfter='X 100%' required />
									</FormItem>
								</>
							);
						} else if (couponType === 3) {
							return (
								<FormItem label={t("extensionDays")} required name='extendDays'>
									<InputNumber controls={false} />
								</FormItem>
							);
						}
					}}
				</FormItem>
				<FormItem label={t("worth")} name='worth'>
					<InputNumber controls={false} />
				</FormItem>

				<FormItem label={t("distributionAmount")} required name='grantNum' rules={[{ required: true }]}>
					<InputNumber controls={false} />
				</FormItem>
				<FormItem label={t("limitNum")} required name='limitNum' rules={[{ required: true }]}>
					<InputNumber controls={false} />
				</FormItem>
				<FormItem label={t("startTime")} name='startTime'>
					<DatePicker />
				</FormItem>
				<FormItem label={t("effectiveWay")} name='effectiveWay' required rules={[{ required: true }]}>
					<Select options={EffectiveWayOption} />
				</FormItem>
				<FormItem dependencies={["effectiveWay"]}>
					{({ getFieldValue }) => {
						const effectiveWay = getFieldValue("effectiveWay");
						return effectiveWay === 1 ? (
							<FormItem label={t("endTime")} name='expirationTime' required>
								<DatePicker />
							</FormItem>
						) : (
							<FormItem label={t("effectiveDays")} required name='effectiveDay'>
								<InputNumber controls={false} />
							</FormItem>
						);
					}}
				</FormItem>

				<Form.Item label={t("admin.role")} name='roleIds'>
					<MultiSelectRole />
				</Form.Item>
				<Form.Item label={t("payWay")} name='payTypes'>
					<MultiSelectPayWay />
				</Form.Item>
				<Form.Item label='限制仅以下月卡可用此券' name='subscriptionIds'>
					<SubscriptionCascader isMultiple />
				</Form.Item>
				<FormItem label='是否禁用' name='status'>
					<Radio.Group
						options={[
							{ label: "否", value: 1 },
							{ label: "是", value: 2 },
						]}
					></Radio.Group>
				</FormItem>
				<FormItem label='' className='additionalRulesContainer'>
					<FormItem label='仅在提前至少7天续费时可用' name='isUsableOnlyWith7DaysAdvanceRenewal' className='flexFormItem'>
						<Radio.Group
							options={[
								{ label: "是", value: 1 },
								{ label: "否", value: 2 },
							]}
						></Radio.Group>
					</FormItem>
				</FormItem>
				<Divider style={{ color: "#888" }}>{t("additionalInfo")}</Divider>
				<FormItem label={t("image")} name='url'>
					<UploadImg size='750*422' />
				</FormItem>
				{false && (
					<FormItem label='生成二维码保存地址' name='appletQCCodePath'>
						<Input />
					</FormItem>
				)}
				<FormItem label={t("remark")} name='remark'>
					<Input.TextArea />
				</FormItem>
				<FormItem label={t("useRule")} name='useRule'>
					<MyEditor />
				</FormItem>
				<FormItem className='lastSubmit'>
					<Button type='primary' htmlType='submit'>
						提交
					</Button>
				</FormItem>
			</Form>
		</Container>
	);
};

const Container = styled.div`
	width: 100%;
	height: 100%;
	box-sizing: border-box;
	padding: 30px;
	padding-right: 300px;

	/* .additionalRulesContainer {
		width: 100%;
		display: flex;
		flex-direction: row;
	}
	.flexFormItem {
		display: flex;
		flex-direction: row !important;
	} */

	.ant-input-number,
	.ant-input-number-group-wrapper {
		width: 100%;
	}

	.editorName {
		width: 100% !important;
		min-height: 300px;
	}
	.ant-picker {
		width: 100%;
	}
	.lastSubmit {
		display: flex;
		justify-content: flex-start;
	}
`;
